import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { catchError, concatMap, from, map, Observable, of, Subject } from 'rxjs';
import { AppState } from 'src/app/@store/app.reducers';
import { DocumentBinary, DocumentData, DocumentumStoreFileDetailsIntern } from 'src/app/models/documentum/documentum.model';
import { DocumentumService } from './documentum.service';
import { ActivarLoadingAction, DesactivarLoadingAction, MostrarMensajeAvisoAction, MostrarMensajeErrorAction } from 'src/app/@store/actions';
import _ from 'lodash';
import { IMimeTypeMap } from 'src/app/models/mime-type-map.models';
import { ModulosDocumentumUtilService } from './modulos-documento-util.service';

@Injectable({ providedIn: 'root' })
export abstract class HandlerDocumentumClass {

    protected abstract getArchivos(): any[];
    protected abstract setArchivos(archivos: any[]): void;
    public _$unsubscribe = new Subject<void>();

    public metadatosDocumentum = inject(ModulosDocumentumUtilService);

    constructor(
        public store: Store<AppState>,
        public documentumService: DocumentumService,
    ) {
    }

    guardarDocumento(documento?: DocumentumStoreFileDetailsIntern) {
        let resultGuardado = [];
        const peticiones = this.getArchivos().filter(doc => {
            return doc.objectId === null
        });

        if (peticiones.length == 0) {
            return this.store.dispatch(MostrarMensajeAvisoAction({ titulo: "", mensaje: "No tienes archivos pendientes por cargar" }))
        }

        const requestPeticiones = peticiones.reduce((acc, curr: DocumentumStoreFileDetailsIntern, index) => {
            const fecha = moment().format("YYYY-MM-DD") + "T00:00:00";
            if (curr != null) {
                const request: DocumentumStoreFileDetailsIntern = {
                    documentName: curr?.documentName,
                    title: curr?.title,
                    subject: curr?.subject,
                    contentType: curr?.contentType,
                    storagePath: curr?.storagePath,
                    documentalType: curr?.documentalType,
                    attributes: {
                        cve_consec: curr?.attributes?.cve_consec,
                        cve_doc: curr?.attributes?.cve_doc,
                        cve_modulo: curr?.attributes?.cve_modulo,
                        cve_submodulo: curr?.attributes?.cve_submodulo,
                        descripcion: curr?.attributes?.descripcion,
                        estatus: curr?.attributes?.estatus,
                        fecha: curr?.attributes?.fecha,
                        fecha_alta: curr?.attributes?.fecha_alta,
                        fecha_estatus: curr?.attributes?.fecha_estatus,
                        id_exp: curr?.attributes?.id_exp,
                        id_movto: curr?.attributes?.id_movto,
                        identific: curr?.attributes?.identific,
                        referencia: curr?.attributes?.referencia,
                        rfc: curr?.attributes?.rfc,
                        tipo_documento: curr?.attributes?.tipo_documento,
                        tipo_movimiento: curr?.attributes?.tipo_movimiento,
                        versiones: curr?.attributes?.versiones,
                    },
                    base64: curr?.base64,
                    fullBase64: curr?.base64,
                    size: curr?.size,
                    link: curr?.link,
                    objectId: curr?.objectId || null,
                    siniestro: curr?.siniestro
                };
                acc.push(this.guardarDocumentoDocumentum(request).pipe(
                    map(result => ({ ...result, error: result?.headerResponse?.responseCode != 'OK', details: result?.headerResponse?.responseDescription })),
                    catchError(error => {
                        return of({ error: true, message: `Error en el elemento ${index + 1}`, details: error });
                    })))
            }
            return acc
        }, []);

        this.store.dispatch(ActivarLoadingAction());
        from(requestPeticiones).pipe(
            concatMap((peticion: any) => peticion)
        ).subscribe({
            next: (value: any) => {
                resultGuardado = [...resultGuardado, { ...value }];
            },
            complete: () => {
                this.store.dispatch(DesactivarLoadingAction());
                if (resultGuardado.some(result => result["error"] ? result["error"] : false)) {
                    this.store.dispatch(MostrarMensajeAvisoAction({ titulo: 'Aviso', mensaje: "Ocurrió un error al guardar los archivos. El nombre de los documentos y/o imágenes no debe contener caracteres especiales como ()[]{}%$." }));
                } else {
                    this.store.dispatch(MostrarMensajeAvisoAction({ titulo: 'Guardado', mensaje: 'La información se guardó correctamente.' }));
                }
            },
            error: (error) => {
                this.store.dispatch(MostrarMensajeErrorAction({ titulo: 'Error', mensaje: 'La información no se pudo guardar correctamente' }));
                this.store.dispatch(DesactivarLoadingAction());
            }
        })
    }

    procesarEliminacion(seccion: string, indexGeneral: number, documentoEntrada: any) {
        if (!documentoEntrada?.objectId) {
            this.borrarDocumento(seccion, indexGeneral, documentoEntrada)
        } else {
            this.eliminarDocumento(documentoEntrada);
        }
    }

    borrarDocumento(seccion: string, indexGeneral: number, documentoEntrada) {
        const docsActuales = this.getArchivos().filter((_, index) => index !== indexGeneral);
        this.setArchivos(docsActuales);
    }

    eliminarDocumento(documentoEntrada: any) {
        this.store.dispatch(ActivarLoadingAction());
        this.documentumService.delete(documentoEntrada?.objectId).subscribe({
            next: (resp) => {
                this.store.dispatch(DesactivarLoadingAction());
                this.store.dispatch(MostrarMensajeAvisoAction({ titulo: 'Guardado', mensaje: 'La información se guardó correctamente.' }));
            }, error: err => {
                this.store.dispatch(DesactivarLoadingAction());
                this.store.dispatch(MostrarMensajeErrorAction({ titulo: 'Error', mensaje: 'La información no se pudo guardar correctamente' }));
            }
        });
    }

    descargarDocumento(documento) {
        this.store.dispatch(ActivarLoadingAction());
        this.documentumService.getOneFile(documento?.objectId).subscribe((resp) => {
            if (resp.headerResponse.responseCode !== 'OK') {
                this.store.dispatch(DesactivarLoadingAction());
            } else {
                const archivo = resp.businessResponse.getDocumentOutcome;
                const nombreDocumento = archivo.documentData.documentName
                const ligaDocumento = `data:${archivo.documentData.contentType};base64,${archivo.documentBinary.documentContent}`
                const downloadLink = document.createElement("a")
                const detallesTipoMime = IMimeTypeMap.find(itm => itm.type === archivo.documentData.contentType)
                downloadLink.href = ligaDocumento
                downloadLink.download = nombreDocumento + ((detallesTipoMime !== undefined) ? detallesTipoMime.extension : '')
                downloadLink.click()
            }
            this.store.dispatch(DesactivarLoadingAction());
        }, error => {
            this.store.dispatch(DesactivarLoadingAction());
        })
    }

    public guardarDocumentoDocumentum(detallesArchivo: DocumentumStoreFileDetailsIntern): Observable<any> {
        const extension = detallesArchivo.documentName ? detallesArchivo.documentName.substring(detallesArchivo.documentName.lastIndexOf('.') + 1).normalize() : null
        const contentType = detallesArchivo.contentType ? detallesArchivo.contentType?.split(":")?.[1] : null;
        const filename = extension !== null ? String(detallesArchivo.documentName.replace(`.${extension}`, '')).normalize() : String(detallesArchivo.documentName).normalize()
        const finalName = this.limpiarCadenaParaImg(filename);
        const documentData: DocumentData = {
            documentName: finalName,
            title: finalName,
            subject: finalName,
            contentType: contentType,
            storagePath: detallesArchivo.storagePath,
            documentalType: detallesArchivo.documentalType,
            attributes: {
                attribute: [
                    {
                        attributeName: 'cve_consec',
                        attributeValue: detallesArchivo.attributes.cve_consec
                    },
                    {
                        attributeName: 'cve_doc',
                        attributeValue: detallesArchivo.attributes.cve_doc
                    },
                    {
                        attributeName: 'cve_modulo',
                        attributeValue: detallesArchivo.attributes.cve_modulo
                    },
                    {
                        attributeName: 'cve_submodulo',
                        attributeValue: detallesArchivo.attributes.cve_submodulo
                    },
                    {
                        attributeName: 'descripcion',
                        attributeValue: finalName.normalize()
                    },
                    {
                        attributeName: 'estatus',
                        attributeValue: detallesArchivo.attributes.estatus
                    },
                    {
                        attributeName: 'fecha',
                        attributeValue: detallesArchivo.attributes.fecha
                    },
                    {
                        attributeName: 'fecha_alta',
                        attributeValue: detallesArchivo.attributes.fecha_alta
                    },
                    {
                        attributeName: 'fecha_estatus',
                        attributeValue: detallesArchivo.attributes.fecha_estatus
                    },
                    {
                        attributeName: 'id_exp',
                        attributeValue: detallesArchivo.attributes.id_exp
                    },
                    {
                        attributeName: 'id_movto',
                        attributeValue: detallesArchivo.attributes.id_movto
                    },
                    {
                        attributeName: 'identific',
                        attributeValue: detallesArchivo.attributes.identific.normalize()
                    },
                    {
                        attributeName: 'referencia',
                        attributeValue: detallesArchivo.attributes.referencia.normalize()
                    },
                    {
                        attributeName: 'rfc',
                        attributeValue: detallesArchivo.attributes.rfc
                    },
                    {
                        attributeName: 'tipo_documento',
                        attributeValue: detallesArchivo.attributes.tipo_documento.normalize()
                    },
                    {
                        attributeName: 'tipo_movimiento',
                        attributeValue: detallesArchivo.attributes.tipo_movimiento.normalize()
                    },
                    {
                        attributeName: 'versiones',
                        attributeValue: detallesArchivo.attributes.versiones
                    }
                ]
            }
        };

        const documentBinary: DocumentBinary = {
            documentContent: detallesArchivo.base64,
            size: detallesArchivo.size,
        };
        return this.documentumService.store(documentData, documentBinary);
    }

    crearDocumentoDocumentm(siniestro: string, cve_doc: string, documentos: any[]): DocumentumStoreFileDetailsIntern[] {
        const fecha = moment().format("YYYY-MM-DD") + "T00:00:00";
        return documentos.map(doc => {
            const contentType = IMimeTypeMap.find(x => doc.nombre.endsWith(x.extension))?.type;
            return {
                documentName: doc.nombre,
                title: doc.nombre,
                subject: doc.nombre,
                contentType: contentType,
                storagePath: "/atlas/expgnrl02/ComplementoWeb",
                documentalType: "atlas_expgnrl02",
                attributes: {
                    cve_consec: doc.rowid,
                    cve_doc: cve_doc,
                    cve_modulo: "2",
                    cve_submodulo: "1",
                    descripcion: "Complemento Siniestros",
                    estatus: "01",
                    fecha: fecha,
                    fecha_alta: fecha,
                    fecha_estatus: fecha,
                    id_exp: cve_doc,
                    id_movto: "1",
                    identific: this.metadatosDocumentum.generarCadenaParametros(cve_doc, doc.rowid),
                    referencia: siniestro,
                    rfc: "SIN RFC",
                    tipo_documento: this.evaluarTipoArchivo(doc.nombre),
                    tipo_movimiento: "Complemento Siniestros",
                    versiones: "1",
                },
                base64: doc.fotografia,
                fullBase64: doc.fotografia,
                size: "0",
                link: "",
                objectId: null,
                siniestro: siniestro,
            }
        })
    }

    evaluarTipoArchivo(nombreArchivo: string): 'FOTO' | 'DOCUMENTO' {
        if (!nombreArchivo) {
            return 'DOCUMENTO';
        }

        const extensionesImagen = [
            'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg'
        ];

        const extension = nombreArchivo
            .toLowerCase()
            .split('.')
            .pop()
            ?.trim();

        return extension && extensionesImagen.includes(extension)
            ? 'FOTO'
            : 'DOCUMENTO';
    }

    limpiarCadenaParaImg(valor: string): string {
        if (!valor) {
            return '';
        }

        return valor
            .trim()
            .replace(/[()\[\]{}%$.]/g, '')  // elimina caracteres especiales
            .replace(/\s+/g, '_');          // reemplaza espacios por _
    }
}
